import moment from "moment";
import type { TFunction } from "i18next";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import useStore from "@/stores";
import { getClassroomStore } from "stores/classroomStore";

/**
 * Unix timestamp (seconds) for today at the given time (HHMM), with seconds zero (minute boundary).
 * Use when passing expiry to handleStartClassSession so the store receives a unix timestamp.
 */
export const endTimeIntToUnixTimestamp = (endTimeInt: number): number => {
	const now = new Date();
	const endTimeHour = Math.floor(endTimeInt / 100);
	const endTimeMinute = endTimeInt % 100;
	const endAtMinute = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endTimeHour, endTimeMinute, 0, 0);
	return Math.floor(endAtMinute.getTime() / 1000);
};

/**
 * Converts a unix timestamp to a formatted time string with proper zero-padding.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 * @param use24Hour - When true, use HH:mm (no AM/PM); otherwise locale 12-hour (LT).
 * @returns Formatted time string or null if expiry is falsy
 */
const formatUnixTimeToString = (expiry?: number | null, use24Hour = false): string | null => {
	if (!expiry) return null;

	const unixMoment = moment.unix(expiry);
	if (use24Hour) {
		return unixMoment.format("HH:mm");
	}

	let meridianTime = unixMoment.format("LT");

	// Add leading zero for single-digit hours
	if (meridianTime[1] === ":") {
		meridianTime = "0" + meridianTime;
	}

	return meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time.
 * @param expiry
 * @param verbose
 * @param isMonitoring
 */
export const generateClassExpiryStr = (expiry: number, verbose = false, isMonitoring = false, use24Hour = false): string | null => {
	const meridianTime = formatUnixTimeToString(expiry, use24Hour);
	if (!meridianTime) return null;

	return verbose ? `${!isMonitoring ? "Class" : "Monitoring"} until ${meridianTime}` : meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time for the tile on the overview page.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 * @param t - i18next translate function (e.g. from useTranslation)
 * @param use24Hour - When true, use HH:mm in the formatted time segment
 */
export const generateClassEndsStr_tile = (expiry: number | null | undefined, t: TFunction, use24Hour = false): string | null => {
	const meridianTime = formatUnixTimeToString(expiry, use24Hour);
	if (!meridianTime) return null;

	return t("overview.classEndsTile", { time: meridianTime });
};

/**
 * Generates session status text based on class end time and monitoring status.
 * @param classroomId - ID of the classroom
 * @param verbose - Whether to use verbose format
 */
export const generateSessionStatusTxt = (classroomId: string, verbose = false, use24Hour = false): string | null => {
	const classroomData = getClassroomStore(useStore.getState()).getClassroomV2(classroomId);
	const isMonitoring = classroomData.session?.isMonitoring;
	const classEndTime = getClassEndTimeUnix(classroomData);

	if (classEndTime === null) {
		return null;
	}

	return generateClassExpiryStr(classEndTime, verbose, isMonitoring ?? false, use24Hour);
};

/**
 * Determines the class end time based on the classroom details.
 * If the class is in monitoring mode, it checks the session end time.
 * If not in monitoring mode, it checks the session end time.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class end time in unix format (seconds since epoch) or null if not found.
 */
export const getClassEndTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	const sessionEndTime = classroomDetails.session?.sessionEndTime;
	const isMonitoring = classroomDetails.session?.isMonitoring;
	if (isMonitoring) {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		}
	} else {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		}
	}
	return null;
};

/**
 * Determines the class start time based on the classroom details.
 * If the class is in monitoring mode, it checks the session start time.
 * If not in monitoring mode, it checks the session start time.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class start time in unix format (seconds since epoch) or null if not found.
 */
export const getClassStartTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	if (classroomDetails !== undefined && Object.entries(classroomDetails).length > 0) {
		const currentTime = new Date().getTime() / 1000;
		const sessionStartTime = classroomDetails.session?.sessionStartTime;
		if (classroomDetails.session?.isMonitoring) {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}
			return null;
		} else {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}
		}
	}
	return null;
};
